import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link as MuiLink,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import InvoicesService from "../../services/invoicesService";
import OrdersService from "../../services/ordersService";

const formatDate = (d) => {
  if (!d) return "—";
  const date = new Date(d);
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-GB");
};

const formatMoney = (v) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "EUR" }).format(
    v || 0
  );

// Compact label + value pair. Replaces the legacy TextField look so static
// invoice data reads as data, not as an editable form.
const DataRow = ({ label, value, copyable }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: "180px 1fr" },
      gap: { xs: 0.25, sm: 2 },
      py: 1,
      borderBottom: (t) => `1px dashed ${t.palette.divider}`,
      "&:last-of-type": { borderBottom: 0 },
    }}
  >
    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4, alignSelf: "center" }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        color: "text.primary",
        fontWeight: 500,
        fontFamily: copyable ? "ui-monospace, SFMono-Regular, Menlo, monospace" : undefined,
        fontSize: copyable ? 12 : 14,
        wordBreak: "break-all",
      }}
    >
      {value || <Box component="span" sx={{ color: "text.disabled" }}>—</Box>}
    </Typography>
  </Box>
);

const SectionTitle = ({ children }) => (
  <Typography
    variant="overline"
    sx={{
      display: "block",
      fontWeight: 700,
      color: "text.secondary",
      letterSpacing: 0.8,
      mb: 1,
    }}
  >
    {children}
  </Typography>
);

export const InvoicePage = (props) => {
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down("md"));
  const invoiceService = InvoicesService();
  const orderService = OrdersService();

  const [printing, setPrinting] = useState(null); // 'invoice' | 'pod' | 'regenerate' | null
  const [linksDialog, setLinksDialog] = useState({ open: false, links: [] });
  const [errorToast, setErrorToast] = useState(null);
  const [infoToast, setInfoToast] = useState(null);
  const [podMissing, setPodMissing] = useState(false);

  const item = props.item || {};
  const invoiceGuid = item.id || item.invoiceId;
  const lines = Array.isArray(item.postedSalesInvoiceLines)
    ? item.postedSalesInvoiceLines
    : [];

  const printInvoicePdf = async () => {
    setPrinting("invoice");
    try {
      if (!invoiceGuid) {
        setErrorToast("This invoice has no document identifier, so the PDF can't be downloaded.");
        return;
      }
      await invoiceService.getInvoicePdf(invoiceGuid);
    } catch (e) {
      setErrorToast("Could not download the invoice PDF. Please try again.");
    } finally {
      setPrinting(null);
    }
  };

  const printPod = async () => {
    setPrinting("pod");
    setPodMissing(false);
    try {
      const itemsCsv = lines.map((it) => it.lineObjectNumber).join(",");
      let files = [];
      try {
        files = (await orderService.getPinkSheetShipmentName(item.orderNumber, itemsCsv)) || [];
      } catch (_) {
        files = [];
      }

      if (Array.isArray(files) && files.length > 0) {
        // Shipments PDFs live in the `fitterapp-pdf-shipments` container.
        const links = (await orderService.printPinkSheets(files)) || [];
        if (links.length > 0) setLinksDialog({ open: true, links });
        else setPodMissing(true);
      } else {
        // Fallback: try the consolidated sales-order PDF. The BFF returns
        // either the (encrypted) blob filename OR an absolute URL — depends
        // on which code path generated it. Handle both. JsonConvert wraps
        // strings in quotes; trim them defensively.
        const raw = await invoiceService.getPinkSheetSalesOrderName(item.orderNumber);
        const cleaned = typeof raw === "string" ? raw.replace(/^"|"$/g, "").trim() : null;
        if (cleaned && cleaned.toLowerCase() !== "null") {
          const fullUrl = /^https?:\/\//i.test(cleaned)
            ? cleaned
            : `https://mtsnstorage.blob.core.windows.net/fitterapp-pdf/${cleaned}`;
          window.open(fullUrl, "_blank", "noopener,noreferrer");
        } else {
          setPodMissing(true);
        }
      }
    } catch (e) {
      console.error("printPod failed", e);
      setErrorToast("Could not retrieve proof of delivery. Please try again.");
    } finally {
      setPrinting(null);
    }
  };

  const regeneratePod = async () => {
    if (!item.orderNumber) return;
    setPrinting("regenerate");
    try {
      const generatedUrl = await invoiceService.regeneratePinkSheet(item.orderNumber);
      if (generatedUrl && /^https?:\/\//i.test(generatedUrl)) {
        setPodMissing(false);
        setInfoToast("Proof of delivery regenerated.");
        window.open(generatedUrl, "_blank", "noopener,noreferrer");
      } else {
        setErrorToast("Could not regenerate the POD. Please try again later.");
      }
    } catch (e) {
      console.error("regeneratePod failed", e);
      setErrorToast("Could not regenerate the POD. Please try again later.");
    } finally {
      setPrinting(null);
    }
  };

  return (
    <Box>
      {/* Top bar — back + actions */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Button
          variant="text"
          color="inherit"
          startIcon={<ArrowBackOutlinedIcon />}
          onClick={props.onBack}
          sx={{ color: "text.secondary", alignSelf: "flex-start" }}
        >
          Back to invoices
        </Button>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            color="primary"
            startIcon={
              printing === "invoice" ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DescriptionOutlinedIcon />
              )
            }
            disabled={!invoiceGuid || printing !== null}
            onClick={printInvoicePdf}
          >
            Invoice PDF
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={
              printing === "pod" ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <LocalShippingOutlinedIcon />
              )
            }
            disabled={!item.orderNumber || printing !== null}
            onClick={printPod}
          >
            POD
          </Button>
        </Stack>
      </Stack>

      {/* POD-missing banner with regenerate CTA */}
      {podMissing && (
        <Alert
          severity="info"
          icon={<InfoOutlinedIcon fontSize="inherit" />}
          sx={{ mb: 2, borderRadius: 2 }}
          action={
            <Button
              size="small"
              color="primary"
              variant="contained"
              startIcon={
                printing === "regenerate" ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <RefreshOutlinedIcon />
                )
              }
              disabled={printing !== null}
              onClick={regeneratePod}
            >
              Regenerate POD
            </Button>
          }
        >
          The proof of delivery PDF for order <strong>{item.orderNumber}</strong> hasn&apos;t been generated yet. You can regenerate it now from the Fitter Global service.
        </Alert>
      )}

      {/* Header strip: invoice number + status pills */}
      <Card
        sx={{
          mb: 3,
          p: { xs: 2, md: 3 },
          border: (t) => `1px solid ${t.palette.divider}`,
          boxShadow: "none",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase" }}>
              Invoice
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.25, lineHeight: 1.1 }}>
              {item.number || "—"}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                label={formatDate(item.postingDate)}
                sx={{ bgcolor: "background.default" }}
              />
              {item.orderNumber && (
                <Chip
                  size="small"
                  label={`Order: ${item.orderNumber}`}
                  sx={{ bgcolor: "background.default" }}
                />
              )}
              {item.serviceType && (
                <Chip
                  size="small"
                  label={item.serviceType.trim()}
                  sx={{
                    bgcolor: "rgba(245, 166, 35, 0.18)",
                    color: "text.primary",
                    fontWeight: 600,
                  }}
                />
              )}
              {item.closed && (
                <Chip
                  size="small"
                  label="Paid"
                  sx={{
                    bgcolor: "rgba(27, 122, 62, 0.14)",
                    color: "#1b7a3e",
                    fontWeight: 700,
                  }}
                />
              )}
            </Stack>
          </Box>
        </Stack>
      </Card>

      {/* Two-column body: details left, summary right */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) 320px" },
          gap: 3,
          alignItems: "start",
          mb: 3,
        }}
      >
        {/* Details */}
        <Card
          sx={{
            p: { xs: 2, md: 3 },
            border: (t) => `1px solid ${t.palette.divider}`,
            boxShadow: "none",
          }}
        >
          <SectionTitle>Customer</SectionTitle>
          <Box sx={{ mb: 3 }}>
            <DataRow label="Sell-to" value={`${item.sellToCustomerNumber || ""} · ${item.sellToCustomerName || ""}`} />
            <DataRow label="Bill-to" value={`${item.billToCustomerNumber || ""} · ${item.billToCustomerName || ""}`} />
            <DataRow label="Purchase order" value={item.purchaseOrderNumber} />
          </Box>

          <SectionTitle>Fitter & Location</SectionTitle>
          <Box sx={{ mb: 3 }}>
            <DataRow label="Fitter" value={item.fitterName ? `${item.fitterNumber || ""} · ${item.fitterName}` : null} />
            <DataRow label="Location" value={item.location} />
            <DataRow label="Warehouse" value={item.locationCode} />
            <DataRow label="Salesperson" value={item.salesPersonCode} />
          </Box>

          <SectionTitle>Vehicle</SectionTitle>
          <Box>
            <DataRow label="Vehicle reg" value={item.vehicleRegistrationNumber} />
            <DataRow label="Mileage" value={item.mileage ? String(item.mileage) : null} />
            <DataRow label="ReTorque" value={item.reTorque ? "Yes" : "No"} />
            <DataRow label="Document id" value={invoiceGuid} copyable />
          </Box>
        </Card>

        {/* Summary */}
        <Card
          sx={{
            p: { xs: 2, md: 3 },
            border: (t) => `1px solid ${t.palette.divider}`,
            boxShadow: "none",
            position: { md: "sticky" },
            top: { md: 80 },
          }}
        >
          <SectionTitle>Summary</SectionTitle>
          <Stack spacing={1.25} sx={{ mb: 2 }}>
            <Stack direction="row" alignItems="baseline" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Subtotal
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {formatMoney(item.amountExclVAT)}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="baseline" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                VAT
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {formatMoney(item.vatAmount)}
              </Typography>
            </Stack>
          </Stack>
          <Divider />
          <Stack
            direction="row"
            alignItems="baseline"
            justifyContent="space-between"
            sx={{ mt: 2 }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Total
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                color: "text.primary",
                letterSpacing: -0.4,
              }}
            >
              {formatMoney(item.amountInclVAT)}
            </Typography>
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 0.5, textAlign: "right" }}
          >
            {item.currencyCode || "EUR"} · incl. VAT
          </Typography>

          <Divider sx={{ my: 2 }} />

          <SectionTitle>Payment</SectionTitle>
          <Stack spacing={1.25}>
            <Stack direction="row" alignItems="baseline" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Due date
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {formatDate(item.dueDate)}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="baseline" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Terms
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, textAlign: "right" }}>
                {item.paymentTerms || "—"}
              </Typography>
            </Stack>
          </Stack>
        </Card>
      </Box>

      {/* Lines */}
      <Card
        sx={{
          overflow: "hidden",
          border: (t) => `1px solid ${t.palette.divider}`,
          boxShadow: "none",
        }}
      >
        <Box sx={{ p: { xs: 2, md: 3 }, pb: 1.5 }}>
          <SectionTitle>Lines ({lines.length})</SectionTitle>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "background.default" }}>
                {!isCompact && (
                  <TableCell sx={{ fontWeight: 700, textTransform: "uppercase", fontSize: 11, letterSpacing: 0.5 }}>
                    Line
                  </TableCell>
                )}
                {!isCompact && (
                  <TableCell sx={{ fontWeight: 700, textTransform: "uppercase", fontSize: 11, letterSpacing: 0.5 }}>
                    Object
                  </TableCell>
                )}
                <TableCell sx={{ fontWeight: 700, textTransform: "uppercase", fontSize: 11, letterSpacing: 0.5 }}>
                  Description
                </TableCell>
                {!isCompact && (
                  <TableCell sx={{ fontWeight: 700, textTransform: "uppercase", fontSize: 11, letterSpacing: 0.5 }}>
                    UoM
                  </TableCell>
                )}
                <TableCell align="right" sx={{ fontWeight: 700, textTransform: "uppercase", fontSize: 11, letterSpacing: 0.5 }}>
                  Qty
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, textTransform: "uppercase", fontSize: 11, letterSpacing: 0.5 }}>
                  Unit price
                </TableCell>
                {!isCompact && (
                  <TableCell align="right" sx={{ fontWeight: 700, textTransform: "uppercase", fontSize: 11, letterSpacing: 0.5 }}>
                    Line total
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((l, i) => (
                <TableRow key={`${l.lineNumber}-${i}`} hover>
                  {!isCompact && <TableCell>{l.lineNumber}</TableCell>}
                  {!isCompact && (
                    <TableCell sx={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 12 }}>
                      {l.lineObjectNumber}
                    </TableCell>
                  )}
                  <TableCell sx={{ fontSize: 13 }}>{l.description}</TableCell>
                  {!isCompact && <TableCell>{l.unitOfMeasureCode}</TableCell>}
                  <TableCell align="right">{l.quantity}</TableCell>
                  <TableCell align="right">{formatMoney(l.unitPrice)}</TableCell>
                  {!isCompact && (
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {formatMoney(l.lineAmountExclVAT)}
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {lines.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={isCompact ? 3 : 7}
                    align="center"
                    sx={{ py: 4, color: "text.secondary" }}
                  >
                    No invoice lines
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* POD links dialog */}
      <Dialog
        open={linksDialog.open}
        onClose={() => setLinksDialog({ open: false, links: [] })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Download proof of delivery</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This order has multiple shipments. Click any PDF to open it.
          </Typography>
          <Stack spacing={1}>
            {linksDialog.links.map((link, i) => {
              const fileName = (() => {
                try {
                  return new URL(link).pathname.split("/").pop();
                } catch (_) {
                  return `Shipment ${i + 1}`;
                }
              })();
              return (
                <MuiLink
                  key={link}
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  sx={{
                    p: 1.25,
                    borderRadius: 1,
                    border: (t) => `1px solid ${t.palette.divider}`,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    color: "text.primary",
                    "&:hover": { bgcolor: "background.default" },
                  }}
                >
                  <PrintOutlinedIcon fontSize="small" sx={{ color: "primary.main" }} />
                  {fileName}
                </MuiLink>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinksDialog({ open: false, links: [] })}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(errorToast)}
        autoHideDuration={5000}
        onClose={() => setErrorToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity="error" onClose={() => setErrorToast(null)}>
          {errorToast}
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(infoToast)}
        autoHideDuration={4000}
        onClose={() => setInfoToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity="success" onClose={() => setInfoToast(null)}>
          {infoToast}
        </Alert>
      </Snackbar>
    </Box>
  );
};
